import type { Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { trackAppcuesEvent } from "@expert/monitoring";
import { useEffect, useMemo, useState } from "react";

import type { Group } from "./types";

import { FLAG_NAME_MAP } from "./defaults";
export interface ContextProps {
    agentSite?: string;
    appName: "expert-assist-standalone" | "expert-workspace";
    employeeId: string;
    environment: string;
    lob?: string;
    partner: Partner;
    source: "expert-assist" | "expert-ui";
}

export interface FeatureFlagProps<T extends Record<string, unknown>> {
    analyticGroup: Group;
    context: ContextProps;
    features: T;
    logger: Logger;
}

export function useFlags<T extends Record<string, unknown>>({
    analyticGroup: group,
    context,
    features,
    logger: loggerProp,
}: FeatureFlagProps<T>) {
    const [updatedFeatures, setUpdatedFeatures] = useState(features);
    const logger = useMemo(() => loggerProp.child({ module: "useFlags" }), [loggerProp]);

    const { dispatcher } = useReactAnalytics();

    useEffect(() => {
        const { employeeId, partner } = context;

        if (!employeeId || !partner) {
            logger.info({ context }, "Feature Flags | missing context for fetching flags");
            return;
        }

        const fetchFeatures = async () => {
            const featureKeys = Object.keys(features);

            logger.info({ context, featureKeys }, "Feature Flags | fetching flags");

            const promises = featureKeys.map(async (feature) => {
                // Use mapped flag name if available, otherwise use the feature key as-is
                const actualFlagName = FLAG_NAME_MAP[feature] ?? feature;
                const data = await getFeature(actualFlagName, { ...context });
                return { [feature]: data };
            });

            try {
                const results = await Promise.all(promises);

                let resultsObject = { ...features };

                results.forEach((result) => {
                    // don't overwrite features if data value comes back undefined from barvidently
                    if (Object.values(result)[0] !== undefined) {
                        resultsObject = { ...resultsObject, ...result };
                    }
                });

                setUpdatedFeatures(resultsObject);

                logger.info({ context, features: resultsObject }, "Feature Flags | fetched flags");

                void dispatcher.withExtra({ partner }).dispatchBusinessEvent("FeatureFlagResults", {
                    ...context,
                    features: resultsObject,
                    group,
                });
                trackAppcuesEvent("FeatureFlagResults", { ...context, features: resultsObject, group });
            } catch (error: unknown) {
                setUpdatedFeatures({ ...features });
                logger.warn({ context, error }, "Feature Flags | error fetching flags");
            }
        };

        void fetchFeatures();
        // oxlint-disable-next-line exhaustive-deps
    }, [context, features]);

    return updatedFeatures;
}
